/**
 * Helper hooks for common service combinations
 * 
 * These hooks provide convenient access to commonly used service combinations
 * following the dependency injection patterns.
 */

import { useServiceRegistry } from '@stores/AppStateContext';
import { CrudServices, FileServices, DataServices, AuthServices, EventServices, AllServices } from '../services/types/ServiceDependencies';

/**
 * Hook for CRUD operations (auth + data client services)
 */
export const useCrudServices = (): CrudServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    authService: serviceRegistry.getAuthService(),
    dataClientService: serviceRegistry.getDataClientService()
  };
};

/**
 * Hook for file operations (auth + data client + storage services)
 */
export const useFileServices = (): FileServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    authService: serviceRegistry.getAuthService(),
    dataClientService: serviceRegistry.getDataClientService(),
    storageService: serviceRegistry.getStorageService()
  };
};

/**
 * Hook for data-only operations (data client service)
 */
export const useDataServices = (): DataServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    dataClientService: serviceRegistry.getDataClientService()
  };
};

/**
 * Hook for auth-only operations (auth service)
 */
export const useAuthServices = (): AuthServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    authService: serviceRegistry.getAuthService()
  };
};

/**
 * Hook for event-driven operations (auth + data client + event services)
 */
export const useEventServices = (): EventServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    authService: serviceRegistry.getAuthService(),
    dataClientService: serviceRegistry.getDataClientService(),
    eventService: serviceRegistry.getEventService()
  };
};

/**
 * Hook for operations requiring all services
 */
export const useAllServices = (): AllServices => {
  const serviceRegistry = useServiceRegistry();
  return {
    authService: serviceRegistry.getAuthService(),
    dataClientService: serviceRegistry.getDataClientService(),
    storageService: serviceRegistry.getStorageService(),
    eventService: serviceRegistry.getEventService()
  };
};